<template>
  <b-modal ref="modal" size="xl" hide-footer scrollable
           :modal-class="modalClass"
           body-class="itm-body" header-class="itm-header" @hidden="onHidden">
    <!-- Title bar carries the totals of the WHOLE filtered set: count (real
         FTDs when grouping, matching the KPI card) + sum incl. family rows -->
    <template #modal-title>
      <div class="itm-head">
        <span class="itm-head__title">{{ title }}</span>
        <span class="itm-head__chips" v-if="visible">
          <span class="itm-chip itm-chip--count">
            <span class="itm-chip__label">{{ groupFtdFamily ? $t('salesDashboard.ftd') : $t('menu.monetaries') }}</span>
            <span class="itm-chip__value">{{ groupFtdFamily && ftdCount != null ? ftdCount : totalCount }}</span>
          </span>
          <span class="itm-chip itm-chip--sum">
            <span class="itm-chip__label">{{ $t('monetary.totalDeposits') }}</span>
            <span class="itm-chip__value">{{ convertToEURO(sum) }}</span>
          </span>
        </span>
      </div>
    </template>
    <monetaries-table v-if="visible" :filters="filters" :columns="columns" :has-selection="false" :lazy="false"
                      :group-ftd-family="groupFtdFamily"
                      @total="v => totalCount = v" @sum="v => sum = v" @ftd-count="v => ftdCount = v"/>
  </b-modal>
</template>

<script>
import MonetariesTable from '@/components/tables/MonetariesTable';
import mixinBase from '@/components/mixin/mixinBase';

/**
 * Drill-down used by the Client/User Insights stat cards: shows the raw
 * monetary transactions behind a KPI, using the standard MonetariesTable.
 * For the FTD-family drill (ftdFamily filter) the rows are grouped per
 * client — his FTD_A/B… deposits expand under his FTD row.
 */
export default {
  name: 'InsightTransactionsModal',
  components: { MonetariesTable },
  mixins: [mixinBase],
  props: {
    // Optional extra class on the modal — lets a host page (e.g. the Command
    // Center) apply its own contrast skin without affecting other pages.
    modalClass: { type: String, default: '' },
  },
  data: () => ({
    visible: false,
    title: '',
    filters: {},
    totalCount: 0,
    sum: 0,
    ftdCount: null,
  }),
  computed: {
    groupFtdFamily() {
      return this.filters && this.filters.ftdFamily === true;
    },
    columns() {
      const cols = [
        { key: 'id', enable: true },
        { key: 'clientDetails', enable: true },
        { key: 'clientDetails.accountId', enable: true },
        { key: 'amount', enable: true },
        { key: 'currency', enable: true },
        { key: 'type', enable: true },
        { key: 'action', enable: true },
        { key: 'depositorUserId', enable: true },
        { key: 'isFtd', enable: true },
        { key: 'registrationTime', enable: true },
        { key: 'status', enable: true },
      ];
      if (this.groupFtdFamily) {
        // FTD vs FTD_A/B… distinction next to the isFtd flag
        cols.splice(9, 0, { key: 'ftdType', enable: true });
      }
      return cols;
    },
  },
  methods: {
    show(title, filters) {
      this.title = title;
      this.filters = filters || {};
      this.totalCount = 0;
      this.sum = 0;
      this.ftdCount = null;
      this.visible = true;
      this.$refs.modal.show();
    },
    onHidden() {
      this.visible = false;
      this.filters = {};
    },
  },
};
</script>

<style lang="scss">
.itm-body {
  min-height: 320px;
  /* Never outgrow the viewport: header/title and the table's pagination must
     always stay reachable — the body scrolls internally instead. */
  max-height: calc(100vh - 190px);
  overflow-y: auto;
}
/* Title bar: title left, total chips right, close button untouched */
.itm-header .modal-title {
  flex: 1;
  min-width: 0;
}
.itm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
  padding-right: 0.75rem;
}
.itm-head__title {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.itm-head__chips {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.itm-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 0.45rem;
  padding: 0.22rem 0.75rem;
  border-radius: 999px;
  color: #fff;
  box-shadow: 0 4px 12px -6px rgba(20, 24, 50, 0.5);

  &--count { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%); }
  &--sum { background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%); }

  &__label {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.9;
  }
  &__value {
    font-size: 0.95rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
}
</style>
